import { authApi } from 'app/services/api';
import accountsJwtService, { jwtManager } from 'app/services/jwtService';
import jwtDecode from 'jwt-decode';

import { ACTIVATION_TOKEN_CLAIM, ACTIVATION_TOKEN_CLAIM_VALUE } from 'constants/auth';
import { getCompanyId, getDomainName } from 'utils/domain';
import { logger } from 'utils/logger';
import { reportNativeError } from 'utils/native/nativeTelemetry';
import {
  buildPublicKeyOptions,
  getPasskeyRpId,
  isPasskeyCanceledError,
  serializeCredentialResponse,
  WEBAUTHN_KIND,
} from 'utils/webAuthn';

import { LOGIN_ERROR, LOGIN_LOADING, LOGIN_SUCCESS } from './login.actions';
import { setMasterDataAction } from './master.actions';

const SIGNIN_PASSKEY_REQUEST = 'SIGNIN_PASSKEY_REQUEST';
const SIGNIN_PASSKEY_SUCCESS = 'SIGNIN_PASSKEY_SUCCESS';
const SIGNIN_PASSKEY_FAILURE = 'SIGNIN_PASSKEY_FAILURE';

const completeSignIn = async (tokenData, dispatch) => {
  await jwtManager.setToken(tokenData);

  const master = await accountsJwtService.signInWithToken();

  dispatch(setMasterDataAction(master));
  dispatch({ type: LOGIN_SUCCESS });

  return master;
};

export const fetchSignInMethodAction = () => async () => {
  const { data } = await authApi.getSignInMethod({ domainname: getDomainName() });

  return data;
};

export const submitPasswordSignInAction =
  ({ email, password }) =>
  async dispatch => {
    dispatch({ type: LOGIN_LOADING });

    try {
      const { data } = await authApi.signInWithPassword(
        { email, companyId: getCompanyId(), domain: getDomainName() },
        { password },
      );

      if (data?.type === 'SecondFactorRequired') {
        dispatch({ type: LOGIN_SUCCESS });

        return { secondFactorRequired: true, signInRequestId: data.signInRequestId };
      }

      const master = await completeSignIn(data, dispatch);

      return master;
    } catch (error) {
      dispatch({ type: LOGIN_ERROR, payload: error || 'Internal error' });

      return null;
    }
  };

// DEV-4157: Decodes the access token to check for the TokenPurpose=activation claim set by the
// Skynet.Accounts backend when minting a constrained activation-only token for a TPO invitee with
// no password yet.
const extractActivationToken = tokenData => {
  const accessTokenValue = tokenData?.accessValue?.value;

  if (!accessTokenValue) {
    return null;
  }

  try {
    const decoded = jwtDecode(accessTokenValue);
    const isActivation = decoded?.[ACTIVATION_TOKEN_CLAIM] === ACTIVATION_TOKEN_CLAIM_VALUE;

    return isActivation ? accessTokenValue : null;
  } catch {
    return null;
  }
};

export const submitOtpSignInAction =
  ({ requestId, code, rememberDevice, clientId }) =>
  async dispatch => {
    const { data } = await authApi.signInWithOtpRequest({
      requestId,
      code,
      rememberDevice,
      clientId,
    });

    // DEV-4157: When the backend returns an activation-only token (no refresh, activation JWT claim),
    // the user must set a password before completing sign-in. Signal this to the caller instead of
    // calling completeSignIn (which would try to call /api/authorization-context and get rejected).
    const activationToken = extractActivationToken(data);

    if (activationToken) {
      return { requiresPasswordSetup: true, activationToken };
    }

    return completeSignIn(data, dispatch);
  };

// DEV-4157: Called from SetInitialPasswordTab with the in-memory activation token. On success,
// the backend returns a full SecurityToken (access + refresh) → completeSignIn to finish login.
export const submitSetInitialPasswordAction =
  ({ newPassword, activationToken }) =>
  async dispatch => {
    const { data } = await authApi.setInitialPassword({ newPassword, activationToken });

    return completeSignIn(data, dispatch);
  };

export const requestOtpSignInAction =
  ({ email }) =>
  async dispatch => {
    dispatch({ type: LOGIN_LOADING });

    try {
      const { data } = await authApi.createSignInRequest({ email });

      dispatch({ type: LOGIN_SUCCESS });

      return data?.id ?? null;
    } catch (error) {
      dispatch({ type: LOGIN_ERROR, payload: error || 'Internal error' });

      throw error;
    }
  };

export const submitPasskeySignInAction = () => async dispatch => {
  dispatch({ type: SIGNIN_PASSKEY_REQUEST });

  try {
    const { data: options } = await authApi.getPasskeyAssertionOptions();

    const credential = await navigator.credentials.get({
      publicKey: buildPublicKeyOptions(options, WEBAUTHN_KIND.GET),
    });

    const assertionResponseJson = serializeCredentialResponse(credential, WEBAUTHN_KIND.GET);

    const { data: tokenData } = await authApi.signInWithPasskey({
      companyId: getCompanyId(),
      rpId: getPasskeyRpId(),
      domain: getDomainName(),
      assertionResponseJson,
    });

    dispatch({ type: SIGNIN_PASSKEY_SUCCESS });

    const master = await completeSignIn(tokenData, dispatch);

    return master;
  } catch (error) {
    // Surface only GENUINE assertion failures (native shell has no console) so a passkey sign-in failure
    // is diagnosable in App Insights. A user-cancel (web NotAllowedError / iOS canceled 1001) is an
    // expected outcome, not an exception — don't report it (DEV-4084).
    if (!isPasskeyCanceledError(error)) {
      logger.error(
        `[passkey-signin] failed: ${error?.name || 'Error'}: ${error?.message || String(error)} ` +
          `rpId=${getPasskeyRpId()}`,
      );
      reportNativeError(error, { context: 'passkey-signin', rpId: getPasskeyRpId() });
    }

    dispatch({ type: SIGNIN_PASSKEY_FAILURE, payload: error });

    throw error;
  }
};
